/**
 * Global Array prototype extensions for common convenience methods.
 *
 * Import this module once at the application entry point to make
 * these methods available on all arrays:
 *
 * ```ts
 * import "@learnai/tools/prototypes";
 *
 * const items = [1, 2, 3];
 * items.isEmpty(); // false
 * items.flush();   // items is now []
 * ```
 *
 * @module prototypes
 */

declare global {
	interface Array<T> {
		/** Returns `true` when the array has no elements. */
		isEmpty(): boolean;

		/** Removes all elements from the array in place by setting `length` to 0. */
		flush(): void;
	}
}

function defineArrayPrototypeMethod<TArgs extends unknown[], TReturn>(
	name: 'isEmpty' | 'flush',
	value: (this: unknown[], ...args: TArgs) => TReturn,
): void {
	const existing = Object.getOwnPropertyDescriptor(Array.prototype, name);
	if (existing?.value) return;

	Object.defineProperty(Array.prototype, name, {
		value,
		writable: true,
		configurable: true,
		enumerable: false,
	});
}

/** Returns `true` when the array has no elements. */
defineArrayPrototypeMethod('isEmpty', function <T>(this: T[]): boolean {
	return this.length === 0;
});

/**
 * Removes all elements from the array in place by setting its
 * `length` to 0. Unlike reassigning to `[]`, this mutates the
 * original reference so all holders of that reference see the change.
 */
defineArrayPrototypeMethod('flush', function <T>(this: T[]): void {
	this.length = 0;
});

export {};
